import { DomHelpers } from '../../core/DomHelpers.js';
import { parseSearchMask, findBySearchMask } from '../../core/imcmd/SearchMask.js';
import { Loc } from '../../locales/registry.js';
import { ConfigTools } from '../ConfigTools.js';
import { ViewMode } from '../ViewOptions.js';

/**
 * Фасад DOM-операций для ViewWidget.
 * Все выборки автоматически ограничиваются маской view.getUiNotMask().
 */
class ViewDomController {
    constructor(view) {
        this.view = view;
    }
    /** Первый элемент по селектору внутри view. */
    querySelector(selector) {
        return this.view.element.querySelector(selector + this.view.getUiNotMask()) || undefined;
    }
    /** Все элементы по селектору внутри view. */
    querySelectorAll(selector, additionalExcludeMask = '') {
        return Array.from(this.view.element.querySelectorAll(selector + this.view.getUiNotMask() + additionalExcludeMask));
    }
    /** Элемент по data-key. */
    itemByKey(key) {
        return this.querySelector(`[data-key="${key}"]`);
    }
    /** Плейсхолдер по data-place. */
    placeByKey(key) {
        return this.querySelector(`[data-place="${key}"]`);
    }
    /** Показать/скрыть плейсхолдер. */
    showPlace(key, show) {
        const el = this.placeByKey(key);
        if (!el)
            return;
        if (show)
            DomHelpers.show(el);
        else
            DomHelpers.hide(el);
    }
    /** Проставляет служебный класс `_x<ui>` всем `.im-view` внутри текущего view. */
    setXClass() {
        DomHelpers.addClass(this.querySelectorAll(".im-view"), `_x${this.view.ui}`);
    }
    // ──────────────── Скелетоны ────────────────
    /**
     * Вставляет скелетон рядом с элементом.
     * Если передан skeletonName — используем его.
     * Иначе имя вычисляется по классу skeleton_<name> и брейкпоинтам.
     */
    appendSkeleton(elem, skeletonName) {
        // 1) Если имя не передано — вычисляем
        let finalName = skeletonName;
        if (!finalName) {
            const match = (elem.className || "").match(/skeleton_[\w-]*/);
            if (!match)
                return;
            const base = match[0];
            const w = window.innerWidth || document.documentElement.clientWidth || 0;
            const isMobile = elem.classList.contains("mobile") && w < 768;
            const isTablet = elem.classList.contains("tablet") && w >= 768 && w < 1024;
            finalName = isMobile ? `${base}_mobile` : (isTablet ? `${base}_tablet` : base);
        }
        // 2) Защита от дублей (если сразу после уже есть скелетон)
        const next = elem.nextElementSibling;
        if (next && next.classList.contains("skeleton"))
            return;
        // 3) Путь с cfgName (фолбек на "default" на случай пустого viewKey)
        const viewKey = ConfigTools.parseViewKey(this.view.options.viewKey);
        const cfgName = viewKey?.cfgName ?? 'vs'; //vs по умолчанию
        // 4) Создаём и вставляем
        const img = document.createElement("img");
        img.className = "skeleton";
        img.alt = Loc.dataLoading;
        img.decoding = "async"; // подсказываем браузеру декодировать изображение асинхронно
        img.src = `/src/${cfgName}/img/skeletons/${finalName}.svg`;
        elem.insertAdjacentElement("afterend", img);
    }
    /** Обновить DOM в соответствии с текущим режимом view.mode */
    applyViewMode() {
        // диалог: запрет/разрешение закрытия по Escape
        this.view.element.closest('im-dialog')
            ?.setOptions({ closeOnEscape: this.view.mode === ViewMode.view });
        // .loading-container (кроме таблиц) — снять on
        const sel = `.loading-container:not(.im-table .loading-container)${this.view.getUiNotMask()}`;
        this.view.element.querySelectorAll(sel)
            .forEach(el => el.classList.remove('on'));
        // снять busy-класс
        this.view.element.classList.remove('im-view-busy');
        // панель обновится сама
        this.view.updatePanelState();
    }
    /** Установка параметра режима редактирования. */
    setEditModeState(on) {
        const key = `_fv_${this.view.options.dataKey}_editMode`;
        const sel = `input[data-key="${key}"]${this.view.getUiNotMask()}`;
        this.view.element.querySelectorAll(sel)
            .forEach(el => { el.value = on ? 'True' : 'False'; });
    }
    /** Поиск элементов по строке-маске с поддержкой зон (^v,^i,^g,^c,^p,^pi) и @key. */
    findByMask(mask, sourceEl) {
        const raw = (mask ?? '').trim();
        if (!raw)
            return [];
        const sm = parseSearchMask(raw);
        return findBySearchMask(this.view.element, sm, sourceEl, this.view.getUiNotMask());
    }
}

export { ViewDomController };
